import { useEffect, useRef, useState } from "react";
import { ShieldAlert, Clock, ChevronDown, ChevronRight } from "lucide-react";
import { fmtTL, fmtDate, todayIso } from "../../lib/format";
import { flashRecord } from "../../lib/notify";
import { letterKindLabel, letterKindCls, isReturned } from "./letterConst";

const dayDiff = (a, b) => Math.round((new Date(a) - new Date(b)) / 86400000);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const UPCOMING_DAYS = 30;

const CFG = {
  expired: {
    id: "expired", Icon: ShieldAlert, defaultOpen: true, col: "Gecikme",
    filter: (d, t) => d.due_date < t,
    title: (n) => `${n} teminat mektubunun vadesi doldu`,
    cell: (d, t) => `${dayDiff(t, d.due_date)} gün`,
    box: "border-red-300 bg-red-50/70", icon: "text-red-700", title_cls: "text-red-800", hint: "text-red-700/80",
    head: "text-red-700/80", row: "border-red-200 hover:bg-red-100/70 focus:bg-red-100", rowActive: "bg-red-100", cellCls: "text-red-700",
  },
  upcoming: {
    id: "upcoming", Icon: Clock, defaultOpen: false, col: "Kalan",
    filter: (d, t) => d.due_date >= t && d.due_date <= addDays(t, UPCOMING_DAYS),
    title: (n) => `${n} teminat mektubunun vadesi ${UPCOMING_DAYS} gün içinde dolacak`,
    cell: (d, t) => { const n = dayDiff(d.due_date, t); return n === 0 ? "bugün" : `${n} gün kaldı`; },
    box: "border-amber-300 bg-amber-50/70", icon: "text-amber-700", title_cls: "text-amber-800", hint: "text-amber-700/80",
    head: "text-amber-700/80", row: "border-amber-200 hover:bg-amber-100/70 focus:bg-amber-100", rowActive: "bg-amber-100", cellCls: "text-amber-700",
  },
};

// Banka Kredileri: vadesi dolan (kırmızı) / 30 gün içinde dolacak (sarı) teminat mektupları — satıra tıkla → listede kayıt vurgulanır; ESC → bu alana geri dön
export default function ExpiredLettersAlert({ letters, mode = "expired" }) {
  const c = CFG[mode];
  const [open, setOpen] = useState(c.defaultOpen);
  const [active, setActive] = useState(null);
  const boxRef = useRef(null);
  const today = todayIso();
  const rows = (letters || []).filter((d) => d.due_date && !isReturned(d) && c.filter(d, today)).sort((a, b) => a.due_date.localeCompare(b.due_date));
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault(); e.stopPropagation();
      boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      document.querySelector(`[data-testid="${c.id}-letter-${active}"]`)?.focus();
      setActive(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active, c.id]);
  if (!rows.length) return null;
  const go = (d) => { setActive(d.id); flashRecord(d.id); };
  return (
    <div ref={boxRef} className={`rounded-lg border scroll-mt-20 ${c.box}`} data-testid={`${c.id}-letters-alert`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${c.id}-letters-toggle`} className="w-full flex items-center gap-2 px-4 py-2.5 text-left">
        {open ? <ChevronDown className={`w-4 h-4 ${c.icon}`} /> : <ChevronRight className={`w-4 h-4 ${c.icon}`} />}
        <c.Icon className={`w-4 h-4 ${c.icon}`} />
        <span className={`text-sm font-bold ${c.title_cls}`} data-testid={`${c.id}-letters-count`}>{c.title(rows.length)}</span>
        <span className={`text-[11px] ${c.hint}`}>· satıra tıkla → kayda git · ESC ile buraya dön</span>
      </button>
      {open && (
        <div className="px-4 pb-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className={`text-[10px] uppercase tracking-wider ${c.head}`}><th className="text-left py-1 pr-3">Banka</th><th className="text-left py-1 pr-3">Firma</th><th className="text-left py-1 pr-3">Proje</th><th className="text-left py-1 pr-3">Mektup No</th><th className="text-right py-1 pr-3">Tutar</th><th className="text-left py-1 pr-3">Vade</th><th className="text-left py-1">{c.col}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} tabIndex={0} onClick={() => go(d)} onKeyDown={(e) => { if (e.key === "Enter") go(d); }} data-testid={`${c.id}-letter-${d.id}`} title="Teminat Mektupları listesindeki kayda git (ESC ile geri dön)"
                  className={`border-t cursor-pointer focus:outline-none ${c.row} ${active === d.id ? c.rowActive : ""}`}>
                  <td className="py-1.5 pr-3 font-semibold">{d.bank || "—"}</td>
                  <td className="py-1.5 pr-3">{d.company || "—"}</td>
                  <td className="py-1.5 pr-3">{d.project_name || "—"}</td>
                  <td className="py-1.5 pr-3 font-mono">{d.letter_no || "—"}{d.letter_kind && <span className={`ml-1 px-1 py-0.5 rounded text-[9px] font-bold font-sans ${letterKindCls(d.letter_kind)}`}>{letterKindLabel(d.letter_kind)}</span>}</td>
                  <td className="py-1.5 pr-3 text-right font-mono font-semibold">{fmtTL(d.amount)}</td>
                  <td className="py-1.5 pr-3">{fmtDate(d.due_date)}</td>
                  <td className={`py-1.5 font-semibold ${c.cellCls}`} data-testid={`${c.id}-letter-days-${d.id}`}>{c.cell(d, today)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
